import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationsEndpointIdentifier } from './use-get-source-brand-locations-api';

const endpoint = 'ums/v1/crm/management/source-brand-locations';
export const createSourceBrandLocationEndpointIdentifier = endpoint;

interface BodyType {
  brandId: number;
  geoLocationId?: number;
  sourcePlatform: string;
  sourceExternalId: string;
  sourceUrl?: string;
  name: string;
  address?: string;
  coordinates?: string;
  rating?: number;
  ratingsTotal?: number;
  reviewsTotal?: number;
  priceLevel?: number;
  avgCostPerCouple?: number;
  avgCostCurrency?: string;
  businessStatus?: string;
  phoneNumber?: string;
  website?: string;
}

type TData = {
  id: number;
  message: string;
};
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, BodyType>;
type ReturnType = UseMutationResult<TData, TError, BodyType>;

export function useCreateSourceBrandLocationApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async (body: BodyType) => {
    try {
      const { data } = await http.post(endpoint, body);

      toast({
        description: 'Source brand location created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: () => {
      // Only invalidate the main list queries, not individual location details
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationsEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });
    },
    ...otherOptions,
  });
}
